// Typed client for the admin support-inbox surface (PROP-163).
//
// Wraps the admin-gated endpoints under /admin/support over the shared
// `api` from lib/api.ts. Wire shapes match the backend serializers
// field-for-field — keep in sync with:
//   - backend/internal/support/store.go      (Thread / Message / ThreadWithMessages)
//   - backend/internal/adminapi/support.go   (list / detail / verb / reply responses)
//
// Engineering owns data (CLAUDE.md): every field rendered by the FE is
// server-derived. The reply path returns the rehydrated thread + the new
// message so the optimistic patch equals a post-refresh GET — no value is
// ever reconstructed from local state.

import { api } from "./api";

// SupportStatus is the stored thread triage state. "all" is a list filter
// only (never a stored status), kept separate as SupportFilter.
export type SupportStatus = "new" | "replied" | "closed";
export type SupportFilter = SupportStatus | "all";

// SupportDirection — inbound = the user wrote in; outbound = an admin reply.
export type SupportDirection = "inbound" | "outbound";

// SupportSendStatus mirrors constants.SupportSendStatus{Pending,Sent,Failed}
// (backend/internal/constants/support.go). It is the dispatch state of an
// OUTBOUND reply row; inbound rows carry the empty string (the column is
// NULL there, COALESCEd to '' by the store). "" is therefore the
// "no send-status" sentinel — the FE renders no badge for it.
export type SupportSendStatus = "" | "pending" | "sent" | "failed";

// SupportThread mirrors support.Thread. last_message_at is nullable
// (a thread with no message yet); message_count is server-hydrated on
// both the list and detail surfaces.
export interface SupportThread {
  id: string;
  subject: string;
  from_email: string;
  from_name: string;
  status: SupportStatus;
  root_message_id: string;
  last_message_at: string | null;
  message_count: number;
  created_at: string;
  updated_at: string;
}

// SupportMessage mirrors support.Message. The JSON key for the RFC-5322
// References header is `references` (the Go field is ReferencesHeader,
// the column references_header — the wire key is the email-semantic one).
// received_at is nullable (outbound replies carry no received_at).
export interface SupportMessage {
  id: string;
  thread_id: string;
  direction: SupportDirection;
  from_email: string;
  to_email: string;
  body: string;
  message_id: string;
  in_reply_to: string;
  references: string;
  sent_by_admin_id: string;
  // Outbound send-status (PROP-156). send_status is a SupportSendStatus for
  // outbound rows, "" for inbound. send_error carries the last dispatch
  // error (""=none); send_attempts counts dispatch tries; last_attempt_at
  // is the last dispatch timestamp (null until the first try). These travel
  // in BOTH the GET timeline and the reply/retry action responses so the
  // optimistic patch equals a post-refresh GET.
  send_status: SupportSendStatus;
  send_error: string;
  send_attempts: number;
  last_attempt_at: string | null;
  received_at: string | null;
  created_at: string;
}

// SupportThreadWithMessages mirrors support.ThreadWithMessages — a thread
// hydrated with its full message timeline in arrival order. `messages` is
// always a non-nil slice server-side.
export interface SupportThreadWithMessages extends SupportThread {
  messages: SupportMessage[];
}

// GET /admin/support → {threads, status, next_cursor?}
export interface SupportListResponse {
  threads: SupportThread[];
  status: SupportFilter;
  next_cursor?: string;
}

// GET /admin/support/{id} → {thread:{...,messages:[]}}
export interface SupportThreadResponse {
  thread: SupportThreadWithMessages;
}

// POST /admin/support/{id}/close|reopen → {thread, audit_logged}
export interface SupportStatusResponse {
  thread: SupportThread;
  audit_logged: boolean;
}

// POST /admin/support/{id}/reply → {thread:{...,messages:[]}, message, audit_logged}
// The thread is the rehydrated GetThread shape (status=replied, the new
// outbound row already in the timeline); message is that same outbound row
// returned standalone for an optimistic patch.
export interface SupportReplyResponse {
  thread: SupportThreadWithMessages;
  message: SupportMessage;
  audit_logged: boolean;
}

// POST /admin/support/{id}/messages/{msgId}/retry → {thread:{...,messages:[]},
// message, audit_logged} on success (200). The shape is byte-identical to the
// reply response: the rehydrated thread (status flipped back to "replied", the
// re-sent outbound row in the timeline) plus that row standalone for an
// optimistic patch. Non-200s are surfaced as ApiError: 409 (the row is no
// longer `failed` — already sent or mid-flight), 404 (thread/message gone or
// inbound), 502 (re-dispatch failed; the row stays `failed` + retryable),
// 503 (reply transport unconfigured).
export type SupportRetryResponse = SupportReplyResponse;

// listSupportThreads fetches one page of the triage queue, newest-active
// first. status defaults server-side to "all"; limit/cursor are optional.
// The server always emits `threads` as an array, but we normalise a
// missing value to [] so the caller can always `.map`.
export async function listSupportThreads(opts: {
  status?: SupportFilter;
  limit?: number;
  cursor?: string;
} = {}): Promise<SupportListResponse> {
  const params: string[] = [];
  if (opts.status) params.push(`status=${encodeURIComponent(opts.status)}`);
  if (opts.limit) params.push(`limit=${opts.limit}`);
  if (opts.cursor) params.push(`cursor=${encodeURIComponent(opts.cursor)}`);
  const q = params.length ? `?${params.join("&")}` : "";
  const resp = await api.get<SupportListResponse>(`/admin/support${q}`);
  return { ...resp, threads: resp.threads ?? [] };
}

// getSupportThread fetches one thread plus its full message timeline.
export function getSupportThread(id: string): Promise<SupportThreadResponse> {
  return api.get<SupportThreadResponse>(`/admin/support/${encodeURIComponent(id)}`);
}

// replyToSupportThread dispatches an operator reply. The FE supplies only
// the body — the recipient, subject, From and threading headers are all
// resolved server-side. The response carries the rehydrated thread (status
// flipped to "replied", the outbound row in the timeline) plus that row,
// so the caller patches its view from the response and never refetches.
export function replyToSupportThread(id: string, body: string): Promise<SupportReplyResponse> {
  return api.post<SupportReplyResponse>(
    `/admin/support/${encodeURIComponent(id)}/reply`,
    { body },
  );
}

// retrySupportReply re-dispatches a FAILED outbound reply WITHOUT inserting a
// new row — the persisted message already reserves the Message-ID and carries
// the recipient + threading headers, so the server reuses them verbatim. The
// FE supplies only the (threadId, messageId) handles; engineering owns the
// rest. On success the response carries the rehydrated thread (status →
// "replied", the re-sent row marked send_status="sent") plus that row, so the
// caller patches its view from the response and never refetches. Errors
// (409/404/502/503) surface as ApiError for the caller to map to a toast.
export function retrySupportReply(threadId: string, messageId: string): Promise<SupportRetryResponse> {
  return api.post<SupportRetryResponse>(
    `/admin/support/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(messageId)}/retry`,
  );
}

// closeSupportThread sets status='closed'. The optional reason rides into
// admin_audit_log server-side.
export function closeSupportThread(id: string, reason?: string): Promise<SupportStatusResponse> {
  return api.post<SupportStatusResponse>(
    `/admin/support/${encodeURIComponent(id)}/close`,
    reason ? { reason } : undefined,
  );
}

// reopenSupportThread returns a closed thread to the queue (status → "new").
export function reopenSupportThread(id: string, reason?: string): Promise<SupportStatusResponse> {
  return api.post<SupportStatusResponse>(
    `/admin/support/${encodeURIComponent(id)}/reopen`,
    reason ? { reason } : undefined,
  );
}
